import React, { useState, useEffect, useCallback } from 'react';
import { Layout } from '../../components/Layout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { Input } from '../../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../../components/ui/dialog';
import { Alert, AlertDescription } from '../../components/ui/alert';
import { Shield, ShieldAlert, Phone, Database, RefreshCw, Clock, AlertTriangle, CheckCircle, Info, Download, Settings, Lock, Unlock } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function CallCenterSettings() {
  const [settings, setSettings] = useState(null);
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showBackfillDialog, setShowBackfillDialog] = useState(false);
  const [backfillDays, setBackfillDays] = useState(90);
  const [backfillJob, setBackfillJob] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      const token = localStorage.getItem('access_token');
      const headers = { Authorization: `Bearer ${token}` };
      
      const [settingsRes, statusRes] = await Promise.all([
        axios.get(`${API}/settings/call-center`, { headers }),
        axios.get(`${API}/settings/call-center/status`, { headers })
      ]);
      
      setSettings(settingsRes.data);
      setStatus(statusRes.data);
    } catch (err) {
      console.error('Failed to fetch settings:', err);
      toast.error('Failed to load Call Center settings');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const updateSetting = async (key, value) => {
    setSaving(true);
    try {
      const token = localStorage.getItem('access_token');
      await axios.patch(`${API}/settings/call-center`, { [key]: value }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setSettings({ ...settings, [key]: value });
      toast.success('Setting updated');
    } catch (err) {
      console.error('Failed to update setting:', err);
      toast.error(err.response?.data?.detail || 'Failed to update setting');
    } finally {
      setSaving(false);
    }
  };

  const triggerBackfill = async () => {
    try {
      const token = localStorage.getItem('access_token');
      const response = await axios.post(`${API}/settings/call-center/backfill?days=${backfillDays}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setBackfillJob(response.data);
      toast.success(`Backfill started for ${backfillDays} days`);
      setShowBackfillDialog(false);
      
      // Poll for status
      const checkStatus = async () => {
        try {
          const statusRes = await axios.get(`${API}/settings/call-center/backfill/${response.data.job_id}`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          
          if (statusRes.data.status === 'running') {
            setTimeout(checkStatus, 5000);
          } else {
            setBackfillJob(statusRes.data);
            if (statusRes.data.status === 'completed') {
              toast.success(`Backfill completed: ${statusRes.data.results?.total_imported || 0} calls imported`);
              fetchData();
            } else {
              toast.error('Backfill failed');
            }
          }
        } catch (e) {
          console.error('Error checking backfill status:', e);
        }
      };
      
      setTimeout(checkStatus, 5000);
    } catch (err) {
      console.error('Failed to trigger backfill:', err);
      toast.error('Failed to start backfill');
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="p-6">
          <div className="py-8 text-center">Loading...</div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
    <div className="p-6 space-y-6">
      {/* Safe Mode Banner */}
      <Alert className="bg-blue-50 border-blue-200">
        <Shield className="h-5 w-5 text-blue-600" />
        <AlertDescription className="ml-2">
          <span className="font-semibold text-blue-800">Safe Mode Active</span>
          <span className="text-blue-700 ml-2">
            Call Center is in read-only mode. All call data is being ingested from CallRail without disrupting live operations.
          </span>
        </AlertDescription>
      </Alert>

      {/* Status Overview */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Mode</p>
                <p className="text-xl font-bold text-blue-600">
                  {status?.is_safe_mode ? 'Safe Mode' : 'Active'}
                </p>
              </div>
              <Shield className="h-8 w-8 text-blue-500 opacity-50" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Total Calls</p>
                <p className="text-xl font-bold">{status?.total_interactions || 0}</p>
              </div>
              <Phone className="h-8 w-8 text-primary opacity-50" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Today</p>
                <p className="text-xl font-bold">{status?.today_interactions || 0}</p>
              </div>
              <Clock className="h-8 w-8 text-green-500 opacity-50" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Backfilled</p>
                <p className="text-xl font-bold">{status?.backfilled_records || 0}</p>
              </div>
              <Database className="h-8 w-8 text-purple-500 opacity-50" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Call Center Mode */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Shield className="h-5 w-5" />
            Call Center Mode
          </CardTitle>
          <CardDescription>
            Control how Rhino-1 interacts with live call operations
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Safe Mode */}
          <div className="p-4 border rounded-lg bg-green-50 border-green-200">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-full bg-green-100">
                  <CheckCircle className="h-5 w-5 text-green-600" />
                </div>
                <div>
                  <h4 className="font-semibold text-green-800">Safe Mode (Read-Only)</h4>
                  <p className="text-sm text-green-700">
                    Ingests all call data without impacting live operations
                  </p>
                </div>
              </div>
              <Badge className="bg-green-500">Active</Badge>
            </div>
            <div className="mt-4 grid grid-cols-2 gap-4 text-sm">
              <div className="flex items-center gap-2 text-green-700">
                <CheckCircle className="h-4 w-4" />
                <span>View all call data</span>
              </div>
              <div className="flex items-center gap-2 text-green-700">
                <CheckCircle className="h-4 w-4" />
                <span>Analyze sources & regions</span>
              </div>
              <div className="flex items-center gap-2 text-green-700">
                <CheckCircle className="h-4 w-4" />
                <span>Link to contacts & jobs</span>
              </div>
              <div className="flex items-center gap-2 text-green-700">
                <CheckCircle className="h-4 w-4" />
                <span>Recording playback</span>
              </div>
            </div>
          </div>

          {/* Active Mode (Disabled) */}
          <div className="p-4 border rounded-lg bg-gray-50 border-gray-200 opacity-60">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-full bg-gray-200">
                  <Lock className="h-5 w-5 text-gray-500" />
                </div>
                <div>
                  <h4 className="font-semibold text-gray-600">Active Mode (Live Control)</h4>
                  <p className="text-sm text-gray-500">
                    Full call routing, agent assignment, and AI activation
                  </p>
                </div>
              </div>
              <Badge variant="secondary">Coming Soon</Badge>
            </div>
            <div className="mt-4 grid grid-cols-2 gap-4 text-sm text-gray-500">
              <div className="flex items-center gap-2">
                <Lock className="h-4 w-4" />
                <span>Route calls to agents</span>
              </div>
              <div className="flex items-center gap-2">
                <Lock className="h-4 w-4" />
                <span>AI call answering</span>
              </div>
              <div className="flex items-center gap-2">
                <Lock className="h-4 w-4" />
                <span>Queue management</span>
              </div>
              <div className="flex items-center gap-2">
                <Lock className="h-4 w-4" />
                <span>Real-time call control</span>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Data Ingestion Settings */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Database className="h-5 w-5" />
            Data Ingestion
          </CardTitle>
          <CardDescription>
            Configure how call data flows from CallRail into Rhino-1
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <Label>Ingest Webhook Calls</Label>
              <p className="text-sm text-muted-foreground">Receive real-time call events from CallRail</p>
            </div>
            <Switch
              checked={settings?.ingest_webhook_calls ?? true}
              onCheckedChange={(v) => updateSetting('ingest_webhook_calls', v)}
              disabled={saving}
            />
          </div>

          <div className="flex items-center justify-between">
            <div>
              <Label>Ingest SMS Messages</Label>
              <p className="text-sm text-muted-foreground">Receive SMS/text message events</p>
            </div>
            <Switch
              checked={settings?.ingest_webhook_sms ?? true}
              onCheckedChange={(v) => updateSetting('ingest_webhook_sms', v)}
              disabled={saving}
            />
          </div>

          <div className="flex items-center justify-between">
            <div>
              <Label>Auto-Create Contacts</Label>
              <p className="text-sm text-muted-foreground">Create new contact records for unknown callers</p>
            </div>
            <Switch
              checked={settings?.auto_create_contacts ?? true}
              onCheckedChange={(v) => updateSetting('auto_create_contacts', v)}
              disabled={saving}
            />
          </div>

          <div className="flex items-center justify-between">
            <div>
              <Label>Auto-Link to Jobs</Label>
              <p className="text-sm text-muted-foreground">Automatically link calls to related jobs</p>
            </div>
            <Switch
              checked={settings?.auto_link_to_jobs ?? true}
              onCheckedChange={(v) => updateSetting('auto_link_to_jobs', v)}
              disabled={saving}
            />
          </div>

          <div className="flex items-center justify-between">
            <div>
              <Label>Store Raw Payloads</Label>
              <p className="text-sm text-muted-foreground">Keep raw webhook data for debugging</p>
            </div>
            <Switch
              checked={settings?.store_raw_payloads ?? true}
              onCheckedChange={(v) => updateSetting('store_raw_payloads', v)}
              disabled={saving}
            />
          </div>
        </CardContent>
      </Card>

      {/* Historical Backfill */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Download className="h-5 w-5" />
            Historical Data Import
          </CardTitle>
          <CardDescription>
            Import historical call data from CallRail API
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-between p-4 bg-muted/50 rounded-lg">
            <div>
              <p className="font-medium">Last Backfill</p>
              <p className="text-sm text-muted-foreground">
                {status?.last_backfill 
                  ? new Date(status.last_backfill).toLocaleString()
                  : 'Never'
                }
              </p>
              {status?.backfilled_records > 0 && (
                <p className="text-sm text-muted-foreground">
                  {status.backfilled_records} records imported
                </p>
              )}
            </div>
            <Button onClick={() => setShowBackfillDialog(true)}>
              <RefreshCw className="h-4 w-4 mr-2" />
              Import Historical Calls
            </Button>
          </div>

          {backfillJob && backfillJob.status === 'running' && (
            <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <div className="flex items-center gap-2">
                <RefreshCw className="h-4 w-4 animate-spin text-blue-600" />
                <span className="font-medium text-blue-800">Backfill in progress...</span>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Prime AI Settings */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Info className="h-5 w-5" />
            Prime AI Integration
          </CardTitle>
          <CardDescription>
            Configure Prime's access to call center data (read-only in Safe Mode)
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <Label>View Call Data</Label>
              <p className="text-sm text-muted-foreground">Allow Prime to see call history and metrics</p>
            </div>
            <Switch
              checked={settings?.prime_can_view_calls ?? true}
              onCheckedChange={(v) => updateSetting('prime_can_view_calls', v)}
              disabled={saving}
            />
          </div>

          <div className="flex items-center justify-between">
            <div>
              <Label>Generate Insights</Label>
              <p className="text-sm text-muted-foreground">Allow Prime to analyze and provide recommendations</p>
            </div>
            <Switch
              checked={settings?.prime_can_generate_insights ?? true}
              onCheckedChange={(v) => updateSetting('prime_can_generate_insights', v)}
              disabled={saving}
            />
          </div>

          <div className="flex items-center justify-between opacity-50">
            <div>
              <Label>Trigger Actions</Label>
              <p className="text-sm text-muted-foreground">Allow Prime to trigger operational actions</p>
            </div>
            <div className="flex items-center gap-2">
              <Lock className="h-4 w-4 text-muted-foreground" />
              <Switch disabled checked={false} />
            </div>
          </div>
          <p className="text-xs text-muted-foreground">
            Action triggers are disabled in Safe Mode to prevent operational impact
          </p>
        </CardContent>
      </Card>

      {/* Backfill Dialog */}
      <Dialog open={showBackfillDialog} onOpenChange={setShowBackfillDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Import Historical Calls</DialogTitle>
            <DialogDescription>
              Import call history from CallRail. This will fetch calls from the API and import any that aren't already in the system.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Label>Number of Days to Import</Label>
            <Select value={backfillDays.toString()} onValueChange={(v) => setBackfillDays(parseInt(v))}>
              <SelectTrigger className="mt-2">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="30">Last 30 days</SelectItem>
                <SelectItem value="60">Last 60 days</SelectItem>
                <SelectItem value="90">Last 90 days</SelectItem>
                <SelectItem value="180">Last 180 days</SelectItem>
                <SelectItem value="365">Last 365 days</SelectItem>
              </SelectContent>
            </Select>
            <p className="text-sm text-muted-foreground mt-2">
              Existing records will be skipped. Only new calls will be imported.
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowBackfillDialog(false)}>Cancel</Button>
            <Button onClick={triggerBackfill}>
              <Download className="h-4 w-4 mr-2" />
              Start Import
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
    </Layout>
  );
}
